// Contractor-approved exclusion of flagged readings — the one place the
// readings a calculation uses can differ from the readings the meter sent.
//
// The rule (context/nec-methodology.md): 220.87 permits no outlier exclusion,
// so TrueLoad excludes readings only on objective evidence of a data error.
// Concretely:
//   - only a window one of the suspect-peak checks flagged can be excluded
//     (suspectPeaks.ts); there is no free-form date range;
//   - at most MAX_EXCLUDED_HOURS in all, per project; past that the honest
//     answer is corrected data from the utility, not more exclusions;
//   - each exclusion carries a reason from a fixed list, an optional note,
//     the contractor's acknowledgement, who and when;
//   - raw readings are never deleted or altered. Exclusions live beside them
//     (projects.ami_exclusions) and `readingsInEffect` applies them;
//   - the report discloses the raw peak, every window and the peak used.
//
// `readingsInEffect` is the only filter. Everything that computes or shows the
// peak — the project summary, the Existing Load chart and tiles, the
// calculation, completion validation, the report — reads through it.
//
// Calc rules revision 5 (calc/rulesVersion.ts): a project completed under an
// earlier revision never applies exclusions, so a filed report stays filed.

import type { Project } from '../types/project';
import { calcRulesFor } from './calc/rulesVersion';
import { summarizeAmiSeries } from './amiSummary';
import type { SuspectCheck, SuspectWindow, TimeRange } from './ami/suspectPeaks';

/** The first calc rules revision that applies exclusions. */
export const EXCLUSIONS_RULES_REVISION = 5;
/** Total excluded time per project. */
export const MAX_EXCLUDED_HOURS = 72;

export const EXCLUSION_ACKNOWLEDGEMENT =
  'I have reviewed this data and believe these readings do not reflect actual building demand. ' +
  'The excluded readings and original peak will be disclosed on the report.';

export const EXCLUSION_REPORT_STATEMENT =
  'Excluded readings were identified by automated data quality checks and reviewed by the preparer. ' +
  'Original readings are retained and available on request. The AHJ may require the calculation to use the raw peak.';

export type ExclusionReason =
  | 'meter_catchup'
  | 'exceeds_service_capacity'
  | 'utility_estimated'
  | 'daily_energy_spike'
  | 'utility_confirmed';

export const EXCLUSION_REASONS: Array<{ value: ExclusionReason; label: string }> = [
  { value: 'meter_catchup', label: 'Meter catch-up after data gap' },
  { value: 'exceeds_service_capacity', label: 'Exceeds service capacity' },
  { value: 'utility_estimated', label: 'Utility-estimated reading' },
  { value: 'daily_energy_spike', label: 'Abnormal one-day energy spike' },
  { value: 'utility_confirmed', label: 'Utility confirmed data error' },
];

/** The reason a check's own evidence names; the contractor may pick another. */
export const DEFAULT_REASON: Record<SuspectCheck, ExclusionReason> = {
  gap_catchup: 'meter_catchup',
  exceeds_service_capacity: 'exceeds_service_capacity',
  utility_estimated: 'utility_estimated',
  daily_energy_spike: 'daily_energy_spike',
};

export const CHECK_LABELS: Record<SuspectCheck, string> = {
  gap_catchup: 'Data gap followed by catch-up readings',
  exceeds_service_capacity: 'Physically impossible: above the service capacity',
  daily_energy_spike: 'One-day energy spike',
  utility_estimated: 'Utility-estimated readings',
};

export function reasonLabel(reason: string): string {
  return EXCLUSION_REASONS.find(r => r.value === reason)?.label ?? reason;
}

export interface ExcludedWindow extends TimeRange {
  /** The flagged window's id (suspectPeaks.ts). */
  id: string;
  check: SuspectCheck;
  hours: number;
  reason: ExclusionReason;
  note: string;
  user_id: string | null;
  /** Name or email, as the report prints it. */
  user_label: string;
  created_at: string;
  /** The highest reading inside the window when it was excluded. */
  window_peak_kw: number;
  window_peak_timestamp: string;
}

/** projects.ami_exclusions. Null when nothing is excluded. */
export interface AmiExclusions {
  /** The peak of every reading, as the meter sent them. */
  raw_peak_kw: number;
  raw_peak_timestamp: string;
  windows: ExcludedWindow[];
}

type ExclusionSource = Pick<Partial<Project>, 'ami_exclusions' | 'calc_rules_version' | 'status' | 'completed_at'>;

/** True when this project calculates under a revision that applies exclusions. */
export function exclusionsApply(project: ExclusionSource): boolean {
  return calcRulesFor(project) >= EXCLUSIONS_RULES_REVISION;
}

/** The windows in force, or none under an earlier rules revision. */
export function activeExclusions(project: ExclusionSource): ExcludedWindow[] {
  if (!exclusionsApply(project)) return [];
  return project.ami_exclusions?.windows ?? [];
}

function inAny(t: number, ranges: Array<{ s: number; e: number }>): boolean {
  for (const r of ranges) if (t >= r.s && t < r.e) return true;
  return false;
}

/**
 * The readings the calculation uses: every reading, less those inside an
 * excluded window. Returns the same array when nothing is excluded, so callers
 * can memoise on identity.
 */
export function readingsInEffect<T extends { timestamp: string }>(points: T[], project: ExclusionSource): T[] {
  const windows = activeExclusions(project);
  if (windows.length === 0) return points;
  const ranges = windows.map(w => ({ s: Date.parse(w.start), e: Date.parse(w.end) }));
  return points.filter(p => !inAny(Date.parse(p.timestamp), ranges));
}

/** Hours covered by the union of the ranges — overlapping windows count once. */
export function unionHours(ranges: TimeRange[]): number {
  const spans = ranges
    .map(r => [Date.parse(r.start), Date.parse(r.end)] as const)
    .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e) && e > s)
    .sort((a, b) => a[0] - b[0]);
  let total = 0;
  let curS = -Infinity;
  let curE = -Infinity;
  for (const [s, e] of spans) {
    if (s > curE) {
      if (curE > curS) total += curE - curS;
      curS = s;
      curE = e;
    } else if (e > curE) {
      curE = e;
    }
  }
  if (curE > curS) total += curE - curS;
  return total / 3_600_000;
}

export function excludedHours(project: ExclusionSource): number {
  return unionHours(activeExclusions(project));
}

/** True when every reading inside the window is already excluded by another. */
export function windowFullyExcluded<T extends { timestamp: string }>(
  window: TimeRange,
  points: T[],
  project: ExclusionSource
): boolean {
  const s = Date.parse(window.start);
  const e = Date.parse(window.end);
  const inside = points.filter(p => {
    const t = Date.parse(p.timestamp);
    return t >= s && t < e;
  });
  if (inside.length === 0) return true;
  const kept = new Set(readingsInEffect(inside, project));
  return kept.size === 0;
}

/** The project fields that follow from a set of exclusions: the record and the peak in effect. */
function withExclusions(
  project: Partial<Project>,
  points: Array<{ timestamp: string; kw: number }>,
  windows: ExcludedWindow[]
): Partial<Project> {
  const raw = summarizeAmiSeries(points);
  if (windows.length === 0) {
    return { ami_exclusions: null, max_recorded_demand_kw: raw.peakKw, peak_timestamp: raw.peakTimestamp };
  }
  const record: AmiExclusions = {
    raw_peak_kw: raw.peakKw ?? 0,
    raw_peak_timestamp: raw.peakTimestamp ?? '',
    windows,
  };
  const used = summarizeAmiSeries(readingsInEffect(points, { ...project, ami_exclusions: record }));
  return { ami_exclusions: record, max_recorded_demand_kw: used.peakKw, peak_timestamp: used.peakTimestamp };
}

export interface ExclusionRequest {
  reason: ExclusionReason | string;
  note?: string;
  acknowledged: boolean;
  userId: string | null;
  userLabel: string;
  now?: Date;
}

export type ExclusionOutcome =
  | { ok: true; updates: Partial<Project> }
  | { ok: false; error: string };

/**
 * Exclude one flagged window. Refuses anything that is not a window the
 * checks flagged in these readings, an unacknowledged or unreasoned request,
 * a completed report, a project frozen under an earlier rules revision, and
 * anything that would take the total past MAX_EXCLUDED_HOURS.
 */
export function excludeWindow(
  project: Partial<Project>,
  points: Array<{ timestamp: string; kw: number }>,
  flagged: SuspectWindow[],
  window: TimeRange & { id: string; check: SuspectCheck },
  request: ExclusionRequest
): ExclusionOutcome {
  if (project.status === 'completed') {
    return { ok: false, error: 'This report is completed. Reopen the project to change its data.' };
  }
  if (!exclusionsApply(project)) {
    return {
      ok: false,
      error: 'This project was completed under an earlier revision of the calculation rules, which does not apply exclusions.',
    };
  }
  const match = flagged.find(
    f => f.id === window.id && f.check === window.check && f.start === window.start && f.end === window.end
  );
  if (!match) {
    return { ok: false, error: 'Only a window flagged by the data quality checks can be excluded.' };
  }
  const existing = activeExclusions(project);
  if (existing.some(w => w.id === match.id)) {
    return { ok: false, error: 'This window is already excluded.' };
  }
  if (!EXCLUSION_REASONS.some(r => r.value === request.reason)) {
    return { ok: false, error: 'Choose a reason for the exclusion.' };
  }
  if (!request.acknowledged) {
    return { ok: false, error: 'Confirm the acknowledgement to exclude these readings.' };
  }
  const total = unionHours([...existing, match]);
  if (total > MAX_EXCLUDED_HOURS + 1e-9) {
    return {
      ok: false,
      error:
        `Excluding this window would set aside ${Math.round(total)} hours of readings, past the ` +
        `${MAX_EXCLUDED_HOURS}-hour limit. That much suspect data needs corrected interval data: ` +
        'ask the utility for a corrected export and upload it in place of this one.',
    };
  }
  const added: ExcludedWindow = {
    id: match.id,
    check: match.check,
    start: match.start,
    end: match.end,
    hours: match.hours,
    reason: request.reason as ExclusionReason,
    note: (request.note ?? '').trim(),
    user_id: request.userId,
    user_label: request.userLabel,
    created_at: (request.now ?? new Date()).toISOString(),
    window_peak_kw: match.peakKw,
    window_peak_timestamp: match.peakTimestamp,
  };
  return { ok: true, updates: withExclusions(project, points, [...existing, added]) };
}

/** Undo one exclusion. Allowed until the report is completed. */
export function removeExclusion(
  project: Partial<Project>,
  points: Array<{ timestamp: string; kw: number }>,
  id: string
): ExclusionOutcome {
  if (project.status === 'completed') {
    return { ok: false, error: 'This report is completed. Reopen the project to change its data.' };
  }
  const existing = project.ami_exclusions?.windows ?? [];
  if (!existing.some(w => w.id === id)) return { ok: false, error: 'That exclusion is not on this project.' };
  return { ok: true, updates: withExclusions(project, points, existing.filter(w => w.id !== id)) };
}

/** What the report prints under "Data Quality Exclusions"; null omits the section. */
export interface ExclusionDisclosure {
  rawPeakKw: number;
  rawPeakTimestamp: string;
  usedPeakKw: number;
  usedPeakTimestamp: string;
  totalHours: number;
  windows: Array<{
    start: string;
    end: string;
    hours: number;
    check: string;
    reason: string;
    note: string;
    by: string;
    at: string;
  }>;
  statement: string;
}

export function exclusionDisclosure(
  project: Partial<Project>,
  used: { peakKw: number; peakTimestamp: string | null }
): ExclusionDisclosure | null {
  const windows = activeExclusions(project);
  const record = project.ami_exclusions;
  if (windows.length === 0 || !record) return null;
  return {
    rawPeakKw: record.raw_peak_kw,
    rawPeakTimestamp: record.raw_peak_timestamp,
    usedPeakKw: used.peakKw,
    usedPeakTimestamp: used.peakTimestamp ?? '',
    totalHours: unionHours(windows),
    windows: [...windows]
      .sort((a, b) => a.start.localeCompare(b.start))
      .map(w => ({
        start: w.start,
        end: w.end,
        hours: w.hours,
        check: CHECK_LABELS[w.check] ?? w.check,
        reason: reasonLabel(w.reason),
        note: w.note,
        by: w.user_label,
        at: w.created_at,
      })),
    statement: EXCLUSION_REPORT_STATEMENT,
  };
}
